import {
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../core/store/store";
import ampifire from "./ampifire.png";
import paykickstart from "./paykickstart.png";
import type { SubscriptionData } from "../../interfaces/subscriptions.interface";

type Props = {};

const Subscriptions = (props: Props) => {
  const authState = useSelector((state: RootState) => state.auth);
  const getSubscriptionPlanName = (planId: string) =>
    authState.plan?.plans.find((plan) => plan._id === planId)?.name || "Unknown plan";

  const visibleSubs = authState.subscriptions?.subscriptions
    ? authState.subscriptions.subscriptions.filter(
      (subscription) => getSubscriptionPlanName(subscription.planId) !== "Free Subscription"
    )
    : [];

  const sortedSubs = visibleSubs
    ? [...visibleSubs].sort(
      (a, b) => {
        const aDate = new Date(a.endDate || a.startDate).getTime() || 0;
        const bDate = new Date(b.endDate || b.startDate).getTime() || 0;

        return bDate - aDate;
      }
    )
    : [];

  const getDisplayStatus = (subscription: SubscriptionData) => {
    const hasEnded =
      subscription.endDate && new Date(subscription.endDate).getTime() <= Date.now();
    const isEndedPrepaidPaddle =
      subscription.processor === "paddle" &&
      subscription.cardLinked === false &&
      subscription.status === "cancelled" &&
      hasEnded;

    if (isEndedPrepaidPaddle) {
      return {
        color: "warning" as const,
        label: "expired",
      };
    }

    switch (subscription.status) {
      case "active":
        return { color: "success" as const, label: "active" };
      case "trial":
        return { color: "info" as const, label: "trial" };
      case "expired":
        return { color: "warning" as const, label: "expired" };
      case "cancelled":
        return {
          color: "default" as const,
          label:
            subscription.cardLinked === false && !hasEnded
              ? "cancels at period end"
              : "cancelled",
        };
      case "paused":
        return { color: "default" as const, label: "paused" };
      default:
        return { color: "default" as const, label: subscription.status };
    }
  };

  const getProcessorLogo = (processor: string) => {
    switch (processor) {
      case "paykickstart":
        return <img src={paykickstart} width="150px" />;
      case "recurly":
        return <img src={ampifire} width="150px" />;
      case "clickbank":
        return <img src={ampifire} width="150px" />;
      case "paddle":
        return "Paddle";
    }
  };

  if (!authState.subscriptions?.subscriptions) {
    return <></>;
  }
  return (
    <Stack>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="subscription table">
          <TableHead>
            <TableRow>
              <TableCell>Subscriptions</TableCell>{" "}
              {/* Assuming planId represents the name */}
              <TableCell align="right">Start Date</TableCell>
              <TableCell align="right">End Date</TableCell>
              <TableCell align="right">Rebill Date</TableCell>
              <TableCell>Status</TableCell> {/* Left-align for text data */}
              <TableCell>Payment Processor</TableCell>{" "}
              {/* Left-align for text data */}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedSubs &&
              sortedSubs.map((sub) => {
                const displayStatus = getDisplayStatus(sub);
                const showRebillDate =
                  displayStatus.label !== "expired" &&
                  displayStatus.label !== "cancelled" &&
                  displayStatus.label !== "paused";

                return (
                <TableRow key={sub.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell component="th" scope="row">
                    {getSubscriptionPlanName(sub.planId)}
                  </TableCell>
                  <TableCell align="right">
                    {new Date(sub.startDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    {sub.endDate
                      ? new Date(sub.endDate).toLocaleDateString()
                      : "N/A"}
                  </TableCell>
                  <TableCell align="right">
                    {showRebillDate && sub.rebillDate
                      ? new Date(sub.rebillDate).toLocaleDateString()
                      : "N/A"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      color={displayStatus.color}
                      label={displayStatus.label}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{getProcessorLogo(sub.processor)}</TableCell>
                </TableRow>
              )})}
          </TableBody>
        </Table>
      </TableContainer>
      <TableFooter sx={{ my: 4 }}>
        {(!sortedSubs || sortedSubs.length === 0) && (
          <>
            <Stack sx={{ textAlign: "center" }}>No Subscriptions Found</Stack>
          </>
        )}
      </TableFooter>
    </Stack>
  );
};

export default Subscriptions;
